import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { safeNext } from "@/lib/safeNext";
import { useTranslation } from "@/lib/i18n";
import { useMe } from "@/lib/auth";
import { getTenantConfig } from "@/lib/tenant";
import { applyLanguage, isLanguage } from "@/lib/useLanguage";


/**
 * `?lang=es|pt|en` from the funnel's magic link (love → MindfulMe report).
 * Applied right away and saved to the user's profile, so the next visits open in the same language.
 */
async function applyLanguageFromLink(lang: string | null) {
  if (!isLanguage(lang)) return;
  applyLanguage(lang);
  try {
    await api("/api/me/language", { method: "PUT", body: JSON.stringify({ language: lang }) });
  } catch {
    // Not critical: the language is already applied locally.
  }
}

export function AuthCallback() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = params.get("token");
  const langParam = params.get("lang");
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [email, setEmail] = useState("");
  const [reqStatus, setReqStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  // StrictMode dedupe: refs persist across the dev-mode mount → cleanup → re-mount
  // sequence, so we use one to remember which token value has already been
  // dispatched to /api/auth/verify and short-circuit the second effect run.
  // Keying on the token value (not a boolean) keeps the route defensible if a
  // genuinely new token ever arrives without a full unmount.
  const verifiedToken = useRef<string | null>(null);

  const { data: me } = useMe();
  const tenant = getTenantConfig();
  const defaultTarget = tenant.homePath;


  useEffect(() => {
    if (me) {
      const target = safeNext(params.get("next"), defaultTarget);
      void applyLanguageFromLink(langParam).then(() => queryClient.invalidateQueries({ queryKey: ["me"] }));
      navigate(target, { replace: true });
      return;
    }

    if (!token) {
      setStatus("error");
      return;
    }
    if (verifiedToken.current === token) return;
    verifiedToken.current = token;
    (async () => {
      try {
        await api("/api/auth/verify", { method: "POST", body: JSON.stringify({ token }) });
        await applyLanguageFromLink(langParam);
        await queryClient.invalidateQueries({ queryKey: ["me"] });
        const target = safeNext(params.get("next"), defaultTarget);
        navigate(target, { replace: true });
      } catch {
        // If verify fails (e.g. token already claimed on previous render) but me query succeeds:
        try {
          const meData = await queryClient.fetchQuery({
            queryKey: ["me"],
            queryFn: () => api("/api/me"),
          });
          if (meData) {
            const target = safeNext(params.get("next"), defaultTarget);
            navigate(target, { replace: true });
            return;
          }
        } catch {
          // Ignore
        }
        setStatus("error");
      }
    })();
  }, [token, langParam, params, navigate, queryClient, me, defaultTarget]);


  if (status === "loading") {
    return (
      // Quick task 260512-q7q — match Login/CheckEmail: surface-canvas bg +
      // wordmark so the transient logging-in state has brand presence.
      <main className="min-h-dvh flex items-center justify-center bg-surface-canvas px-4 py-12 sm:px-8">
        <div className="flex flex-col items-center gap-6 max-w-[420px] w-full" aria-live="polite">
          {tenant.brandName === "MindfulMe" ? (
            <span className="flex items-center gap-2 font-black text-slate-900 tracking-tight text-2xl sm:text-3xl">
              <span className="relative flex size-7 items-center justify-center">
                <span className="absolute left-0 size-5 rounded-full bg-indigo-600" />
                <span className="absolute right-0 size-5 rounded-full bg-orange-500 opacity-90 mix-blend-multiply" />
              </span>
              <span>Mindful<span className="text-indigo-600">Me</span></span>
            </span>
          ) : (
            <span className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">{tenant.brandName}</span>
          )}
          <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
          <p className="text-base text-foreground">{t("auth_logging_in")}</p>
          <Skeleton className="h-32 w-full rounded-lg" aria-hidden />
        </div>
      </main>
    );
  }

  return (
    // Quick task 260512-q7q — match Login/CheckEmail error treatment:
    // surface-canvas bg + wordmark + elev-2 card.
    <main className="min-h-dvh flex items-center justify-center bg-surface-canvas px-4 py-12 sm:px-8">
      <div className="flex w-full max-w-[420px] flex-col items-center gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
        >
          {tenant.brandName === "MindfulMe" ? (
            <span className="flex items-center gap-2 font-black text-slate-900 tracking-tight text-2xl sm:text-3xl">
              <span className="relative flex size-7 items-center justify-center">
                <span className="absolute left-0 size-5 rounded-full bg-indigo-600" />
                <span className="absolute right-0 size-5 rounded-full bg-orange-500 opacity-90 mix-blend-multiply" />
              </span>
              <span>Mindful<span className="text-indigo-600">Me</span></span>
            </span>
          ) : (
            <span className="text-primary font-bold">{tenant.brandName}</span>
          )}
        </Link>

      <Card className="w-full shadow-elev-2">
        <CardHeader className="text-center">
          <CardTitle>{t("auth_failed_title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-base text-foreground">
            {t("auth_failed_desc")}
          </p>
          
          {reqStatus === "success" ? (
            <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-500 border border-emerald-500/20 text-center">
              {t("login_success")}
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!email.trim()) return;
                setReqStatus("loading");
                setErrorMessage("");
                try {
                  await api("/api/auth/request-magic-link", {
                    method: "POST",
                    body: JSON.stringify({ email: email.trim() }),
                  });
                  setReqStatus("success");
                } catch (err) {
                  setReqStatus("error");
                  const msg = apiErrorMessage(err, "Failed to send link.");
                  if (msg === "user_not_found") {
                    setErrorMessage(t("login_error_user_not_found"));
                  } else if (msg === "token_not_found") {
                    setErrorMessage(t("login_error_token_not_found"));
                  } else {
                    setErrorMessage(msg);
                  }
                }
              }}
              className="space-y-4"
            >
              <div className="space-y-2 text-left">
                <label htmlFor="restore-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                  {t("login_email_label")}
                </label>
                <input
                  id="restore-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login_placeholder")}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={reqStatus === "loading"}
                />
              </div>

              {reqStatus === "error" && (
                <p className="text-sm text-destructive text-center">{errorMessage}</p>
              )}

              <Button
                type="submit"
                variant="default"
                size="touch"
                disabled={reqStatus === "loading"}
                className="w-full"
              >
                {reqStatus === "loading" ? t("login_sending") : t("login_send_btn")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      </div>
    </main>
  );
}
